import { useMemo } from 'react';
import type { AggregationConfig, AggregationType, AggregationResult } from '../../utils/aggregations';
import { aggregate } from '../../utils/aggregations';

export interface UseAggregationOptions<T extends Record<string, unknown>> {
  data: T[];
  aggregations: AggregationConfig;
}

export interface UseAggregationReturn {
  totalAggregations: Record<string, AggregationResult>;
  getColumnAggregation: (field: string) => AggregationResult | undefined;
}

export function useAggregation<T extends Record<string, unknown>>({
  data,
  aggregations
}: UseAggregationOptions<T>): UseAggregationReturn {
  const totalAggregations = useMemo(() => {
    const results: Record<string, AggregationResult> = {};

    Object.entries(aggregations).forEach(([field, type]) => {
      const values = data.map(row => row[field]);
      const value = aggregate(values, type);

      results[field] = {
        field,
        type,
        value
      };
    });

    return results;
  }, [data, aggregations]);

  const getColumnAggregation = (field: string): AggregationResult | undefined => {
    return totalAggregations[field];
  };

  return {
    totalAggregations,
    getColumnAggregation
  };
}

export function calculateGroupAggregation<T extends Record<string, unknown>>(
  rows: T[],
  field: string,
  type: AggregationType
): number {
  const values = rows.map(row => row[field]);
  return aggregate(values, type);
}
